
import throttle from 'lodash.throttle';
import ClickOutside from 'vue-click-outside';
import regexps from '@/constants/regexps';
import TextInput from '../text-input';
import Icon from '../../../../icon';
import { ControlEventType } from '../../enums';
import { TextInputProperty, TextInputType } from '../text-input/enums';
import { InputFillType, InputSlotType } from '../../shared/input/enums';
import {
  SelectProperty,
  OptionProperty,
  SelectSlotType,
  SelectedListProperty,
  SelectedListEventType,
} from './enums';
import SelectProps from './mixins/SelectProps';
import { IconType, IconProperty } from '../../../../icon/enums';
import { StyleSizeType } from '../../../../../styles/enums';
import StyleClassNamesBuilder from '../../../../../styles/helpers/StyleClassNamesBuilder';
import { AriaRole, KeyboardKeyCode } from '../../../../../enums';
import SelectSelectedList from './selected-list';

const TEXT_INPUT_REF = 'root-ref';
const OPTIONS_CONTAINER_REF = 'options-container-ref';
const OPTION_TAG_NAME = 'LI';
const REMOVE_ITEM_SELECTOR = 'button';
const INPUT_SELECTOR = 'input';
const INPUT_EVENT_DEBOUNCE_TIME = 100;

export default {
  name: 'Select',
  directives: {
    ClickOutside,
  },
  components: {
    Icon,
    TextInput,
    SelectSelectedList,
  },
  mixins: [SelectProps],
  data() {
    return {
      TEXT_INPUT_REF,
      OPTIONS_CONTAINER_REF,
      SelectProperty,
      SelectedListProperty,
      SelectedListEventType,
      TextInputProperty,
      TextInputType,
      IconType,
      IconProperty,
      InputSlotType,
      SelectSlotType,
      StyleSizeType,
      ControlEventType,
      OptionProperty,
      AriaRole,
      areOptionsVisible: false,
    };
  },
  computed: {
    isMultipleSelectedListVisible() {
      return this[SelectProperty.Multiple];
    },
    hasSelectedMultipleItems() {
      return this.isMultipleSelectedListVisible && !!this[SelectProperty.Value]?.length;
    },
    selectedItemLabel() {
      if (this[SelectProperty.Multiple]) {
        return '';
      }

      if (this[SelectProperty.Filterable]) {
        return this[SelectProperty.InputValue];
      }

      return this.selectedOption?.[OptionProperty.Label];
    },
    selectedOption() {
      return this[SelectProperty.Options].find((option) => (
        option[OptionProperty.Value] === this[SelectProperty.Value]
      ));
    },
    selectClassNames() {
      return new StyleClassNamesBuilder('select')
        .addColorSchemeConditionClassNames(this[SelectProperty.ColorScheme])
        .addConditionClassNames(Object.values(InputFillType), this[SelectProperty.FillType])
        .addConditionClassName('focused', this.areOptionsVisible)
        .addConditionClassName('multiple', this.isMultipleSelectedListVisible)
        .addConditionClassName('multiple-selected', this.hasSelectedMultipleItems)
        .build();
    },
    selectListId() {
      return `${this[SelectProperty.Name].replace(regexps.alphaNumeric, '')}-select-list`;
    },
    filteredOptions() {
      const filteredOptions = this[SelectProperty.Options].filter((option) => {
        const isAlreadySelected = this[SelectProperty.Multiple]
          && this[SelectProperty.Value]?.includes(option[OptionProperty.Value]);

        return !isAlreadySelected;
      });

      return filteredOptions.length ? filteredOptions : [this[SelectProperty.NoValueOption]];
    },
    hasOptionSlot() {
      return !!this.$slots[SelectSlotType.Option];
    },
    chevronIconType() {
      return this.areOptionsVisible ? IconType.ChevronUp : IconType.ChevronDown;
    },
  },
  mounted() {
    const selected = this.selectedOption;
    const inputValue = (selected && selected[OptionProperty.Label]) || '';

    if (inputValue) {
      this[SelectProperty.InputChangeHandler]?.(inputValue);
    }
  },
  methods: {
    getInputRef() {
      return this.$refs[TEXT_INPUT_REF].$el.querySelector(INPUT_SELECTOR);
    },
    getOptionsContainerRef() {
      return this.$refs[OPTIONS_CONTAINER_REF];
    },
    getOptionClassNames(option) {
      const isOptionsSelected = this[SelectProperty.Multiple]
        ? this[SelectProperty.Value]?.includes(option[OptionProperty.Value])
        : option[OptionProperty.Value] === this.value;

      return new StyleClassNamesBuilder('select__option')
        .addConditionClassName('selected', isOptionsSelected)
        .addConditionClassName('disabled', option[OptionProperty.Disabled])
        .build();
    },
    handleOptionClick(option) {
      if (this[SelectProperty.Multiple]) {
        this.multipleOptionSelect(option);
      } else {
        this.singleOptionSelect(option);
      }
    },
    multipleOptionSelect(option) {
      const value = [...(this[SelectProperty.Value] || []), option[OptionProperty.Value]];

      this.$emit(ControlEventType.Change, value);
    },
    singleOptionSelect(option) {
      this.$emit(ControlEventType.Change, option[OptionProperty.Value]);
      this.closeOptions();
      this.getInputRef().focus();
    },
    handleRemoveOption(value) {
      this.$emit(
        ControlEventType.Change,
        this.value.filter((optionValue) => optionValue !== value),
      );
    },
    handleInputClick(event) {
      if (this[SelectProperty.Disabled]) {
        return;
      }

      const isRemoveItemButtonClick = event.target.closest(REMOVE_ITEM_SELECTOR);

      if (!isRemoveItemButtonClick) {
        this.toggleOptionsVisibility();
      }
    },
    handleInputChange(value) {
      this.openOptions();

      this[SelectProperty.InputChangeHandler]?.(value);
    },
    handleInputFocus() {
      this.$emit(ControlEventType.Focus);
    },
    handleBlur(event) {
      if (this.areOptionsVisible && !event.relatedTarget?.closest(`#${this.selectListId}`)) {
        this.$emit(ControlEventType.Blur);
      }
    },
    handleOutsideClick() {
      if (this.areOptionsVisible) {
        this.closeOptions();
        this.$emit(ControlEventType.Blur);
      }
    },
    handleKeyUp(event) {
      switch (event.code) {
        case KeyboardKeyCode.Escape:
          return this.closeOptions();
        case KeyboardKeyCode.Enter:
          return this.handleEnterPress();
        case KeyboardKeyCode.ArrowDown:
          this.openOptions();
          return this.moveFocusForward();
        case KeyboardKeyCode.ArrowUp:
          this.openOptions();
          return this.moveFocusBack();
        default:
          return null;
      }
    },
    handleEnterPress() {
      const currentFocus = this.getCurrentFocus();

      if (this.areOptionsVisible && currentFocus.tagName === OPTION_TAG_NAME) {
        currentFocus.click();
      } else {
        this.toggleOptionsVisibility();
      }
    },
    getCurrentFocus() {
      return document.activeElement;
    },
    moveFocusForward() {
      const currentFocus = this.getCurrentFocus();

      if (currentFocus === this.getInputRef()) {
        this.getOptionsContainerRef()?.firstChild?.focus();
      } else {
        currentFocus.nextElementSibling?.focus();
      }
    },
    moveFocusBack() {
      const currentFocus = this.getCurrentFocus();

      if (currentFocus.tagName === OPTION_TAG_NAME) {
        const elementToFocus = currentFocus.previousElementSibling || this.getInputRef();

        elementToFocus.focus();
      }
    },
    toggleOptionsVisibility() {
      this.setOptionsVisibleValue(!this.areOptionsVisible);
    },
    closeOptions() {
      this.setOptionsVisibleValue(false);
    },
    openOptions() {
      this.setOptionsVisibleValue(true);
    },
    setOptionsVisibleValue: throttle(
      // eslint-disable-next-line func-names
      function (value) { this.areOptionsVisible = value; },
      INPUT_EVENT_DEBOUNCE_TIME,
      { trailing: false },
    ),
  },
};
